import React from 'react'
import { motion } from 'framer-motion'
import { Coins, Zap, Clock, Lock, CheckCircle2 } from 'lucide-react'

const TokenExplanation = () => {
  const benefits = [
    {
      icon: <Zap className="text-yellow-400" size={24} />,
      title: "Pague pelo Uso",
      desc: "Sem mensalidades ocultas. Você só gasta tokens quando executa uma tarefa de IA pesada."
    },
    {
      icon: <Clock className="text-cyan-400" size={24} />,
      title: "Tokens Nunca Expiram",
      desc: "Comprou e não usou? Seus tokens continuam na sua conta para sempre. Sem pressão."
    },
    {
      icon: <Lock className="text-purple-400" size={24} />,
      title: "Acesso Premium",
      desc: "Use GPUs H100 de última geração para Upscaling e Geração sem precisar de um PC da NASA."
    },
    {
      icon: <CheckCircle2 className="text-green-400" size={24} />,
      title: "Controle Total",
      desc: "Saiba exatamente o custo de cada operação antes de clicar. Transparência total."
    }
  ]

  return (
    <div className="w-full max-w-6xl mx-auto mb-20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        {/* Lado Esquerdo: Copy persuasiva */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-yellow-500/10 p-3 rounded-xl border border-yellow-500/20">
              <Coins className="text-yellow-400" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-white">Economia Inteligente</h3>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
            POTÊNCIA DE NUVEM, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
              CUSTO DE BANCA DE JORNAL.
            </span>
          </h2>
          
          <p className="text-gray-400 text-lg leading-relaxed mb-8">
            A Overpixel utiliza um sistema híbrido. Ferramentas como o <strong>Melhorador Desktop</strong> e <strong>Spot White</strong> rodam no seu PC (Grátis). 
            Já o <strong>DTF Factory</strong> e o <strong>Melhorador Cloud</strong> utilizam nossos super-servidores para entregar resultados impossíveis de rodar localmente.
            <br /><br />
            <span className="text-white font-bold">Por que isso é bom?</span> Você tem o melhor dos dois mundos: A velocidade do Desktop para o dia a dia, e a potência da Nuvem para criações complexas.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="mt-1 bg-white/5 p-2 rounded-lg h-fit border border-white/5">
                  {benefit.icon}
                </div>
                <div>
                  <h4 className="text-white font-bold mb-1">{benefit.title}</h4>
                  <p className="text-sm text-gray-400 leading-snug">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Lado Direito: Visual de Comparação ou Exemplo */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 to-purple-500/20 blur-3xl rounded-full opacity-30" />
          
          <div className="relative bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden p-8 shadow-2xl">
            <div className="text-center mb-8">
              <p className="text-gray-400 uppercase tracking-widest text-xs font-bold mb-2">EXEMPLO REAL DE CUSTO</p>
              <h3 className="text-2xl font-bold text-white">Quanto rende 50 Tokens?</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                    <Zap size={20} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-bold">Upscaling 4x (Melhorador)</p>
                    <p className="text-xs text-gray-500">Transformar img ruim em HD</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-yellow-400 font-bold">~10 Imagens</p>
                  <p className="text-xs text-gray-500">5 tokens/img</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400">
                    <CheckCircle2 size={20} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-bold">Spot White (Camada de Branco)</p>
                    <p className="text-xs text-gray-500">Rodando na Nuvem</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-yellow-400 font-bold">~25 Separações</p>
                  <p className="text-xs text-gray-500">2 tokens/img</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5 opacity-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                    <Clock size={20} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-bold">Melhorador Desktop (Local)</p>
                    <p className="text-xs text-gray-500">Usando sua GPU</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-bold">ILIMITADO</p>
                  <p className="text-xs text-gray-500">0 tokens</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-gray-400">
                <span className="text-yellow-400 font-bold">1 Token ≈ R$ 0,10</span> (varia conforme pacote)
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default TokenExplanation
